import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Divider,
  CardHeader,
  CardContent,
  Grid,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Chip,
} from '@mui/material';

import { Link, useParams } from 'react-router-dom';
import { DetailPageHeader } from '../../components/layout/DetailPageHeader';
import {
  Add,
  Cancel,
  Engineering,
  Person,
  VisibilityOutlined,
} from '@mui/icons-material';
import {
  capitalizeWords,
  formatDate,
  formatPhoneNumber,
} from '../../utils/helper';
import {
  GETAmcRequestById,
  GETQuotesForEnquiry,
  UpdateEnquiryStatus,
} from '../../services/requests';
import AMCRequestDetailsCard from './AMCRequestCard';
import AssignTechnicianModal from '../Booking/actions/TechnicianModal';
import AddQuotation from './AddQuotation';
import useSnackbar from '../../hooks/useSnackbar';

export interface AMCRequest {
  _id: string;
  createdAt: string;
  updatedAt: string;

  leadId: string;
  place: string;
  quantity: number;
  comment: string;
  address: string;
  username: string;
  phoneNumber: string;

  userDetails: UserDetails;
  enquiry: Enquiry;
  acDetails: ACDetail[];
  assignedTechnician: Technician;
}

export interface UserDetails {
  _id: string;
  name: string;
  email: string;
  phoneNumber: string;
  countryCode: string;
  profilePhoto: string;
  type?: string;
  isActive: number;
  createdAt: string;
}

export interface ACDetail {
  _id: string;
  acType: string;
  quantity: number;
}

export interface Technician {
  _id: string;
  name: string;
  phoneNumber: string;
  email: string;
  countryCode: string;
  profilePhoto: string;
}

export interface Enquiry {
  _id: string;
  enquiryId: string;
  type: string;
  subType: string;
  status: string;
  noOfAc: number;
  cancelReason?: string;

  addressDetails: {
    house: string;
    street: string;
    city: string;
    state: string;
    zipcode: string;
    saveAs: string;
    landmark: string;
  };

  schedule: {
    slot: string | null;
    date: string | null;
  };
}

const composeRequestAddress = (
  address?: Partial<Enquiry['addressDetails']>,
) => {
  if (!address) return '';
  return [
    address.house,
    address.street,
    address.landmark,
    address.city,
    address.state,
    address.zipcode,
  ]
    .filter(Boolean)
    .join(', ');
};

const AMCRequest = () => {
  const { id } = useParams();
  const [amcRequest, setAmcRequest] = useState<AMCRequest | null>(null);
  const [quotes, setQuotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [openQuoteModal, setOpenQuoteModal] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  useEffect(() => {
    if (id) {
      fetchAMCRequest(id);
    }
  }, [id]);

  useEffect(() => {
    const enquiryId = amcRequest?.enquiry?._id;
    if (!enquiryId) {
      setQuotes([]);
      return;
    }
    GETQuotesForEnquiry(enquiryId).then(setQuotes);
  }, [amcRequest?.enquiry?._id]);

  async function fetchAMCRequest(leadId: string) {
    try {
      setLoading(true);
      setError('');

      const data = await GETAmcRequestById(leadId);

      setAmcRequest(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load request');
    } finally {
      setLoading(false);
    }
  }

  const handleRejectRequest = async () => {
    // Backend resolves the enquiry by its human-readable code, not Mongo _id
    if (!amcRequest?.enquiry?.enquiryId) return;

    try {
      setRejecting(true);
      await UpdateEnquiryStatus(
        amcRequest.enquiry.enquiryId,
        'CANCELLED',
        rejectReason.trim(),
      );
      showSnackbar('AMC request rejected', 'success');
      setOpenRejectDialog(false);
      setRejectReason('');
      if (id) {
        fetchAMCRequest(id);
      }
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : 'Failed to reject request',
        'error',
      );
    } finally {
      setRejecting(false);
    }
  };

  const customer = useMemo(() => {
    if (!amcRequest) return null;

    return {
      _id: amcRequest.userDetails?._id ?? '',

      fullName: amcRequest.userDetails?.name || amcRequest.username,

      contactNumber: amcRequest.phoneNumber,

      customerType: amcRequest.userDetails?.type || 'N/A',

      placeType: amcRequest.place || 'N/A',

      address: amcRequest.address,

      totalAC: amcRequest.quantity,

      inspectionDate: formatDate(amcRequest.enquiry?.schedule?.date),

      inspectionTime: amcRequest.enquiry?.schedule?.slot || 'N/A',

      // `amcRequest.address` is the address actually captured with this AMC
      // lead. `enquiry.addressDetails` is only a snapshot of the customer's
      // most recently created address (set at lead-creation time on the
      // backend) and may not match this specific request, so it's used only
      // as a fallback.
      inspectionAddress:
        amcRequest.address ||
        composeRequestAddress(amcRequest.enquiry?.addressDetails) ||
        'N/A',

      inspector: amcRequest.assignedTechnician?.name || '',

      cancelReason: amcRequest.enquiry?.cancelReason || '',
    };
  }, [amcRequest]);

  const isCancelled = amcRequest?.enquiry?.status === 'CANCELLED';

  if (loading) {
    return (
      <>
        <DetailPageHeader title={'Request Details'} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
          }}
        >
          <CircularProgress sx={{ color: '#E31E25' }} />
        </Box>
      </>
    );
  }

  if (error && !amcRequest) {
    return (
      <>
        <DetailPageHeader title={'Request Details'} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            minHeight: '50vh',
          }}
        >
          <Typography color="error">{error}</Typography>
          <Button
            variant="outlined"
            color="error"
            onClick={() => id && fetchAMCRequest(id)}
          >
            Retry
          </Button>
        </Box>
      </>
    );
  }

  return (
    <>
      <DetailPageHeader title={'Request Details'} />
      <Box
        sx={{
          backgroundColor: '#1C1C1C',
          color: '#fff',
          borderRadius: 2,
          m: 3,
        }}
      >
        <CustomerRequestDetails
          customer={customer}
          status={amcRequest?.enquiry?.status ?? ''}
          type="REGULAR"
        />

        <AMCRequestDetailsCard
          quantity={amcRequest?.quantity ?? 0}
          place={amcRequest?.place ?? ''}
          comment={amcRequest?.comment}
          acDetails={amcRequest?.acDetails ?? []}
        />

        <QuotationsCard quotes={quotes} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          m: 3,
        }}
      >
        {/* Left Button */}
        <Button
          variant="outlined"
          sx={{
            color: '#D32F2F',
            borderColor: '#D32F2F',
            px: 2.5,
            py: 1.2,
            borderRadius: '14px',
            '&:hover': {
              borderColor: '#B71C1C',
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
            },
          }}
          startIcon={<Cancel />}
          disabled={!amcRequest?.enquiry?.enquiryId || isCancelled}
          onClick={() => setOpenRejectDialog(true)}
        >
          Reject Request
        </Button>

        {/* Right Buttons */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            startIcon={<Add />}
            sx={{
              color: '#fff',
              backgroundColor: '#E31E25',
              '&:hover': {
                backgroundColor: '#C62828',
              },
              padding: '10px 20px',
              borderRadius: '14px',
            }}
            disabled={!amcRequest}
            onClick={() => setOpenQuoteModal(true)}
          >
            Add Quotation
          </Button>

          <Button
            startIcon={<Engineering />}
            sx={{
              color: '#fff',
              backgroundColor: '#E31E25',
              '&:hover': {
                backgroundColor: '#C62828',
              },
              padding: '10px 20px',
              borderRadius: '14px',
            }}
            disabled={!amcRequest?.enquiry?._id}
            onClick={() => setOpenAssignModal(true)}
          >
            {amcRequest?.assignedTechnician?.name
              ? 'Reassign Inspector'
              : 'Assign Inspector'}
          </Button>
        </Box>
      </Box>

      {/* Reject confirmation */}
      <Dialog
        open={openRejectDialog}
        onClose={() => !rejecting && setOpenRejectDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reject AMC Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to reject this AMC request? This will mark the
            enquiry as CANCELLED.
          </Typography>
          <TextField
            label="Cancellation reason"
            placeholder="Let the team know why this request is being rejected"
            fullWidth
            multiline
            minRows={2}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenRejectDialog(false)}
            disabled={rejecting}
          >
            Keep Request
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleRejectRequest}
            disabled={rejecting}
          >
            {rejecting ? 'Rejecting…' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign inspector */}
      {amcRequest?.enquiry?._id && (
        <AssignTechnicianModal
          isOpen={openAssignModal}
          onClose={() => setOpenAssignModal(false)}
          bookingId={amcRequest.enquiry._id}
          isEnquiry={true}
          onReload={() => id && fetchAMCRequest(id)}
        />
      )}

      <AddQuotation
        open={openQuoteModal}
        onClose={() => setOpenQuoteModal(false)}
        id={id || ''}
        source="AMC"
        enquiryId={amcRequest?.enquiry?._id}
        onSaved={() => {
          if (id) fetchAMCRequest(id);
          if (amcRequest?.enquiry?._id)
            GETQuotesForEnquiry(amcRequest.enquiry._id).then(setQuotes);
        }}
      />

      {SnackbarComponent}
    </>
  );
};

export default AMCRequest;

// Shown after "Add Quotation" — previously nothing ever read Quote records
// back after they were created (no endpoint existed, and a broken
// `Quote.pre('find')` hook in the model silently excluded every result), so
// submitted quotations appeared to vanish. Reusable by Copper Piping / Free
// Consultancy too since they create Quote records the same way.
export const QuotationsCard = ({ quotes }: { quotes: any[] }) => {
  if (!quotes || quotes.length === 0) return null;

  return (
    <Box sx={{ color: '#fff' }}>
      <CardHeader
        title="Quotations"
        titleTypographyProps={{ fontWeight: 600, fontSize: 18 }}
      />
      <Divider sx={{ borderColor: '#565656' }} />
      <CardContent sx={{ backgroundColor: '#232323', p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {quotes.map((quote) => (
            <Box
              key={quote._id}
              sx={{
                backgroundColor: '#2E2E2E',
                borderRadius: 1,
                px: 2,
                py: 1.5,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography sx={{ color: '#0BE600', fontWeight: 600 }}>
                  ₹{Number(quote.amount || 0).toLocaleString('en-IN')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#9e9e9e' }}>
                  {quote.status} • {formatDate(quote.createdAt)}
                </Typography>
              </Box>
              {quote.inspectionAgent && (
                <Typography variant="body2" sx={{ color: '#ddd', mt: 0.5 }}>
                  Inspection Agent: {quote.inspectionAgent}
                </Typography>
              )}
              {Array.isArray(quote.items) && quote.items.length > 0 && (
                <Box
                  sx={{
                    mt: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                  }}
                >
                  {quote.items.map((item: any, index: number) => (
                    <Box key={index}>
                      <Typography variant="body2" sx={{ color: '#ccc' }}>
                        {item.brand || 'AC'} × {item.quantity || 1} — ₹
                        {Number(item.offeredAmount || 0).toLocaleString(
                          'en-IN',
                        )}
                        {item.age && ` • Age: ${item.age}`}
                        {item.condition && ` • Condition: ${item.condition}`}
                      </Typography>
                      {item.inspectionRemarks && (
                        <Typography
                          variant="caption"
                          sx={{ color: '#9e9e9e', display: 'block' }}
                        >
                          Remarks: {item.inspectionRemarks}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </CardContent>
      <Divider sx={{ borderColor: '#565656' }} />
    </Box>
  );
};

export const CustomerRequestDetails = ({
  customer,
  type,
  status,
}: {
  customer: any | null;
  type?: 'REGULAR' | 'CUSTOMERHISTORY' | 'TECHNICIANHISTORY' | 'VIEWDETAIL';
  status: string;
}) => {
  if (!customer) return null;
  return (
    <>
      {/* Booking Detail Card */}
      <Box
        sx={{
          backgroundColor: 'transparent',
          color: '#fff',
          borderRadius: 2,
        }}
      >
        <CardHeader
          title={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {type === 'VIEWDETAIL' ? (
                  <>
                    <Typography variant="h6">Booking Detail</Typography>
                    <svg
                      onClick={() => {
                        // window.open(`/bookings/edit/${booking?._id}`, '_self');
                      }}
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7 7H6C5.46957 7 4.96086 7.21071 4.58579 7.58579C4.21071 7.96086 4 8.46957 4 9V18C4 18.5304 4.21071 19.0391 4.58579 19.4142C4.96086 19.7893 5.46957 20 6 20H15C15.5304 20 16.0391 19.7893 16.4142 19.4142C16.7893 19.0391 17 18.5304 17 18V17"
                        stroke="#E31E25"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M16 4.99998L19 7.99998M20.385 6.58499C20.7788 6.19114 21.0001 5.65697 21.0001 5.09998C21.0001 4.543 20.7788 4.00883 20.385 3.61498C19.9912 3.22114 19.457 2.99988 18.9 2.99988C18.343 2.99988 17.8088 3.22114 17.415 3.61498L9 12V15H12L20.385 6.58499Z"
                        stroke="#E31E25"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </>
                ) : (
                  <>
                    <Typography variant="h6">Customer Detail</Typography>
                    <Person />
                  </>
                )}
              </Box>
              <Typography variant="body1">
                Status:{' '}
                <span
                  style={{
                    color: status === 'CANCELLED' ? '#E31E25' : '#0BE600',
                  }}
                >
                  {status}
                </span>
              </Typography>
            </Box>
          }
        />
        <Divider sx={{ borderColor: '#565656' }} />
        <CardContent sx={{ backgroundColor: '#232323', p: 3 }}>
          <Grid
            container
            spacing={1}
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              //   border: '2px solid red',
            }}
          >
            <Grid item xs={2} sx={{ color: 'gray' }}>
              Full Name
            </Grid>
            <Grid item xs={4}>
              <Typography
                variant="body2"
                component={Link} // Makes it behave like a link
                to={`/customers/${customer._id}`} // Navigate to technician details
                sx={{
                  color: '#0BE600',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                {capitalizeWords(customer.fullName ?? '')}
              </Typography>
            </Grid>
            <Grid item xs={2} sx={{ color: 'gray' }}>
              Contact Number
            </Grid>
            <Grid item xs={4}>
              <Typography
                variant="body2"
                sx={{
                  color: '#FFFFFF',
                }}
              >
                {formatPhoneNumber(customer.contactNumber || '')}
              </Typography>
            </Grid>
            <Grid item xs={2} sx={{ color: 'gray' }}>
              Customer Type
            </Grid>
            <Grid item xs={4}>
              <Typography
                variant="body2"
                sx={{
                  color: '#FFFFFF',
                }}
              >
                {customer.customerType
                  ? capitalizeWords(String(customer.customerType)).replaceAll(
                      '_',
                      ' ',
                    )
                  : 'N/A'}
              </Typography>
            </Grid>
            {customer.placeType && (
              <>
                <Grid item xs={2} sx={{ color: 'gray' }}>
                  Place Type
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                    {capitalizeWords(String(customer.placeType)).replaceAll(
                      '_',
                      ' ',
                    )}
                  </Typography>
                </Grid>
              </>
            )}
            <Grid item xs={2} sx={{ color: 'gray' }}>
              Inspection Date
            </Grid>
            <Grid item xs={4}>
              <Typography
                variant="body2"
                sx={{
                  color: '#FFFFFF',
                }}
              >
                {customer.inspectionDate}
              </Typography>
            </Grid>

            {/* Left */}
            <Grid item xs={6}>
              <Grid container>
                <Grid item xs={4} sx={{ color: 'gray' }}>
                  Address
                </Grid>

                <Grid item xs={8}>
                  <Typography variant="body2" sx={{ color: '#fff' }}>
                    {customer.address}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            {/* Right */}
            <Grid item xs={6}>
              <Grid container spacing={1}>
                <Grid item xs={4} sx={{ color: 'gray' }}>
                  Inspection Time
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2">
                    {customer.inspectionTime?.replace(/_/g, ' ')}{' '}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            {/* Left */}
            <Grid item xs={6}>
              <Grid container spacing={1}>
                <Grid item xs={4} sx={{ color: 'gray' }}>
                  Total AC
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                    {customer.totalAC}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            {/* Left — empty spacer so Assign Inspector lands on the right */}
            <Grid item xs={6} />

            {/* Right */}
            <Grid item xs={6}>
              <Grid container spacing={1}>
                <Grid item xs={4} sx={{ color: 'gray' }}>
                  Assign Inspector
                </Grid>
                <Grid item xs={8}>
                  {customer?.inspector ? (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                        {customer.inspector}
                      </Typography>
                      <IconButton
                        component={Link}
                        to={`/technicians`}
                        size="small"
                        sx={{ color: '#E31E25', p: 0.25 }}
                        aria-label="View technician profile"
                      >
                        <VisibilityOutlined fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Chip
                      label="N/A"
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(255,255,255,0.08)',
                        color: '#9e9e9e',
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>
            {status === 'CANCELLED' && (
              <>
                <Grid item xs={2} sx={{ color: 'gray' }}>
                  Cancelled Reason
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                    {customer.cancelReason || 'N/A'}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
        <Divider sx={{ borderColor: '#565656' }} />
      </Box>
    </>
  );
};
